/*
 * PRD-037 — Paso de selección de plan del claim (app SEPARADA del panel).
 *
 * Se renderiza dentro de ClaimPage DESPUÉS de claimComplete y de persistir la sesión plena del
 * comercio en localStorage (para que axiosInstance mande el JWT de comercio + id_comercio que el
 * authorizer del gateway espera), y ANTES del redirect final al panel.
 *
 * Diseño mobile-first (V4 inline, sin Tailwind — ver DESIGN.md / PRJ-004):
 * - Tarjetas full-width apiladas en UNA columna en todos los breakpoints → cero overflow horizontal
 *   (precio y CTA en filas propias, nunca se cortan en móvil).
 * - La tarjeta RECOMENDADA (PROFESIONAL) va PRIMERO y resaltada (fondo slate oscuro + borde de
 *   acento + badge), para que sea lo primero que se ve en móvil.
 * - Jerarquía: nombre → precio prominente → pedidos incluidos → features con check → CTA por tarjeta.
 *
 * Reuso de datos: categorizePlans + tipos del panel. El dueño queda FREEMIUM por defecto: el CTA
 * "Continuar con plan gratis" solo ejecuta el redirect al panel sin contratar nada.
 */
import { useEffect, useMemo, useState } from 'react';
import { IconCheck, IconPaperBag } from '@tabler/icons-react';
import { SUBSCRIPTION_TYPES_MAP } from '@utils/constants';
import { formatToCurrency } from '@utils/formats';
import {
  categorizePlans,
  getPlanCategoryDescription,
  type SubscriptionForUI,
} from '@pages/Subscriptionsv2/utils/categorizePlans';
import type { AvailableSubscription } from '@interfaces/general';
import { V4 } from '@components/v4/tokens';
import { partnerEvent, partnerTag } from './analytics';
import { Button, C, Card, ErrorNote, Muted, PageShell, Title } from './ui';
import { useDesktop } from '../utils/hooks/useDesktop';
import { fetchPublicPlans } from './useClaimPlans';
import { ClaimPlanModal } from './ClaimPlanModal';

type Tab = 'monthly' | 'annual';

// Cuántas características listamos por tarjeta (las restantes se ven al contratar / en el panel).
const MAX_FEATURES = 5;

interface ClaimPlanStepProps {
  /** Redirige al panel — lo dispara tanto el CTA gratis como una contratación exitosa. */
  onDone: () => void;
  /** Nombre del comercio recién activado (para personalizar el copy). */
  commerceName?: string;
}

const PlanCard = ({ plan, onChoose }: { plan: SubscriptionForUI; onChoose: () => void }) => {
  const featured = plan.featured;

  // Paleta por estado: la tarjeta recomendada va sobre fondo slate oscuro (texto blanco), el resto
  // sobre blanco con texto slate. Define TODOS los colores derivados para no repetir ternarios.
  const cardBg = featured ? C.primary : C.white;
  const titleColor = featured ? C.white : C.primary;
  const priceColor = featured ? C.white : C.primary;
  const mutedColor = featured ? 'rgba(255,255,255,0.78)' : C.muted;
  const checkColor = featured ? V4.accentLight : C.success;
  const featureColor = featured ? 'rgba(255,255,255,0.9)' : C.muted;

  return (
    <div
      style={{
        background: cardBg,
        border: `1.5px solid ${featured ? C.primary : C.cardBorder}`,
        borderTop: featured ? `4px solid ${C.secondaryLight}` : undefined,
        borderRadius: 12,
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        width: '100%',
        boxSizing: 'border-box',
        boxShadow: featured ? '0 6px 20px rgba(37,55,70,0.18)' : undefined,
      }}
    >
      {/* Fila 1: nombre + badge recomendado (envuelve si no cabe; nunca empuja al precio). */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: titleColor }}>{plan.nombre}</h3>
        {featured ? (
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: C.primary,
              background: C.secondaryLight,
              padding: '3px 9px',
              borderRadius: 9999,
            }}
          >
            Recomendado
          </span>
        ) : null}
      </div>

      {/* Fila 2: precio prominente en su PROPIA fila (no compite por ancho con el nombre). */}
      <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 4 }}>
        {typeof plan.precio === 'number' ? (
          <>
            <span style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.1, color: priceColor }}>
              {formatToCurrency(plan.precio)}
            </span>
            <span style={{ fontSize: 13, color: mutedColor }}>/{plan.periodo_pago === 'anual' ? 'año' : 'mes'}</span>
          </>
        ) : (
          <span style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.1, color: priceColor }}>Gratis</span>
        )}
      </div>

      {/* Fila 3: pedidos incluidos. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: titleColor }}>
        <IconPaperBag size={16} stroke={2} style={{ flexShrink: 0 }} />
        <span style={{ fontSize: 12.5, fontWeight: 500 }}>
          Incluye <strong>{plan.pedidos_maximos || 0} pedidos</strong> mensuales
        </span>
      </div>

      {/* Fila 4: features con check. */}
      {plan.caracteristicas?.length ? (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
          {plan.caracteristicas.slice(0, MAX_FEATURES).map((feature, index) => (
            <li
              key={index}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: featureColor }}
            >
              <IconCheck size={15} stroke={2.5} color={checkColor} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>{feature.descripcion}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Fila 5: CTA full-width por tarjeta. */}
      <Button onClick={onChoose} variant={featured ? 'secondary' : 'primary'} fullWidth style={{ marginTop: 'auto' }}>
        Elegir {plan.nombre}
      </Button>
    </div>
  );
};

const ClaimPlanStep = ({ onDone, commerceName }: ClaimPlanStepProps) => {
  const isDesktop = useDesktop();
  const [plans, setPlans] = useState<AvailableSubscription[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<AvailableSubscription | null>(null);

  useEffect(() => {
    let alive = true;
    fetchPublicPlans()
      .then((res) => {
        if (alive) setPlans(res);
      })
      .catch(() => {
        if (alive) setLoadError('No pudimos cargar los planes. Puedes continuar gratis y elegir uno más tarde.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Mismo mapeo y categorización que el panel (MyAccount): el Profesional es el recomendado en
  // AMBAS cadencias (mensual 'PROFESIONAL' y anual 'PROFESIONAL_A'). Sin plan actual (el dueño
  // recién reclamado aún no contrata nada).
  const categorized = useMemo(() => {
    const profesionalKey = SUBSCRIPTION_TYPES_MAP.PROFESIONAL.key;
    const forUI: SubscriptionForUI[] = (plans ?? [])
      .filter((p) => p.is_public)
      .map((p) => ({
        ...p,
        featured: p.id === profesionalKey || p.id === `${profesionalKey}_A`,
        isCurrentPlan: false,
      }));
    return categorizePlans(forUI);
  }, [plans]);

  const hasAnnual = categorized.annual.length > 0;
  const currentPlans = tab === 'annual' ? categorized.annual : categorized.monthly;

  // La recomendada va PRIMERO (visibilidad en móvil sin scroll), el resto en su orden original.
  const orderedPlans = useMemo(() => {
    const featured = currentPlans.filter((p) => p.featured);
    const rest = currentPlans.filter((p) => !p.featured);
    return [...featured, ...rest];
  }, [currentPlans]);

  return (
    <PageShell>
      <Card>
        <Title
          sub={
            commerceName ? (
              <>
                <strong>{commerceName}</strong> ya está activo. Elige el plan que mejor se adapte a tu negocio — puedes
                empezar gratis y cambiar cuando quieras.
              </>
            ) : (
              'Puedes empezar gratis y cambiar cuando quieras.'
            )
          }
        >
          Elige tu plan
        </Title>

        {loadError ? <ErrorNote>{loadError}</ErrorNote> : null}

        {loading ? (
          <Muted>Cargando planes...</Muted>
        ) : (
          <>
            {hasAnnual ? (
              <div
                style={{
                  display: 'flex',
                  width: isDesktop ? 'auto' : '100%',
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: `1px solid ${C.cardBorder}`,
                  marginBottom: 14,
                }}
              >
                {(['monthly', 'annual'] as Tab[]).map((t) => {
                  const active = tab === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTab(t)}
                      style={{
                        flex: isDesktop ? '0 0 auto' : 1,
                        padding: '9px 16px',
                        fontSize: 12.5,
                        fontWeight: 700,
                        fontFamily: 'Poppins, sans-serif',
                        cursor: 'pointer',
                        border: 'none',
                        background: active ? C.secondary : C.white,
                        color: active ? C.white : C.muted,
                        transition: 'background 150ms, color 150ms',
                      }}
                    >
                      {t === 'monthly' ? 'Mensuales' : 'Anuales (-20%)'}
                    </button>
                  );
                })}
              </div>
            ) : null}

            {orderedPlans.length ? (
              <>
                <Muted style={{ marginTop: 0, marginBottom: 14, fontStyle: 'italic' }}>
                  {getPlanCategoryDescription(tab)}.
                </Muted>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {orderedPlans.map((plan) => (
                    <PlanCard
                      key={plan.id}
                      plan={plan}
                      onChoose={() => {
                        // Analytics: owner picked a paid plan (intent to upgrade). `plan_id` is the
                        // plan key (e.g. PROFESIONAL / PROFESIONAL_A) — non-PII.
                        partnerEvent('claim_plan_selected');
                        partnerTag({ plan_id: plan.id });
                        setSelectedPlan(plan);
                      }}
                    />
                  ))}
                </div>
              </>
            ) : !loadError ? (
              <Muted>No hay planes disponibles por ahora. Puedes continuar gratis.</Muted>
            ) : null}
          </>
        )}

        <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.cardBorder}` }}>
          <Button variant="ghost" fullWidth onClick={onDone}>
            Continuar con plan gratis
          </Button>
          <Muted style={{ textAlign: 'center' }}>Tu plan gratis ya está activo. Puedes mejorarlo cuando quieras.</Muted>
        </div>
      </Card>

      {selectedPlan ? (
        <ClaimPlanModal
          plan={selectedPlan}
          isOpen={Boolean(selectedPlan)}
          onClose={() => setSelectedPlan(null)}
          onActivated={onDone}
        />
      ) : null}
    </PageShell>
  );
};

export default ClaimPlanStep;
